import { createServer, Model, Response } from 'miragejs';
import hotelsData from './data/hotels.json';
import countriesData from './data/countries.json';

export function makeServer({ environment = 'development' } = {}) {
  let hotels = hotelsData.map((hotel) => ({
    ...hotel,
    images: hotel.images.map((image) => ({ ...image })),
    benefits: [...hotel.benefits],
  }));

  let server = createServer({
    environment,

    models: {
      user: Model,
      // Define other models here if needed
    },

    seeds(server) {
      server.create('user', {
        id: '1',
        email: 'user1@example.com',
        password: 'password1',
        firstName: 'John',
        lastName: 'Doe',
        fullName: 'John Doe',
        phone: '1234567890',
        country: 'USA',
        role: 'customer',
        isActive: true,
        isPhoneVerified: true,
        isEmailVerified: true,
      });
      server.create('user', {
        id: '2',
        email: 'user2@example.com',
        password: 'password2',
        firstName: 'Jane',
        lastName: 'Doe',
        fullName: 'Jane Doe',
        phone: '0987654321',
        country: 'UK',
        role: 'customer',
        isActive: true,
        isPhoneVerified: false,
        isEmailVerified: true,
      });
      server.create('user', {
        id: '3',
        email: 'admin@novastay.com',
        password: 'admin123',
        firstName: 'Novastay',
        lastName: 'Admin',
        fullName: 'Novastay Admin',
        phone: '2065550100',
        country: 'USA',
        role: 'admin',
        isActive: true,
        isPhoneVerified: true,
        isEmailVerified: true,
      });
    },

    routes() {
      this.namespace = 'api';

      // Add a logged-in user state to the server
      let loggedInUser = null;
      let adminBookings = [
        {
          bookingId: 'BKG123',
          bookingDate: '2024-01-10',
          hotelName: 'Hyatt Regency Seattle',
          checkInDate: '2024-01-20',
          checkOutDate: '2024-01-25',
          totalFare: '$578.00',
          status: 'Completed',
          guestName: 'John Doe',
          email: 'user1@example.com',
        },
        {
          bookingId: 'BKG124',
          bookingDate: '2024-01-03',
          hotelName: 'The Westin Seattle',
          checkInDate: '2024-02-15',
          checkOutDate: '2024-02-20',
          totalFare: '$1,645.00',
          status: 'Confirmed',
          guestName: 'Jane Doe',
          email: 'user2@example.com',
        },
        {
          bookingId: 'BKG125',
          bookingDate: '2024-01-11',
          hotelName: 'Hyatt Regency Chicago',
          checkInDate: '2024-03-01',
          checkOutDate: '2024-03-05',
          totalFare: '$1,436.00',
          status: 'Cancelled',
          guestName: 'John Doe',
          email: 'user1@example.com',
        },
      ];

      const adminOnly = () =>
        loggedInUser && loggedInUser.role === 'admin'
          ? null
          : new Response(403, {}, { errors: ['Admin access required'] });

      const serializeUser = (user) => {
        const { password, ...safeUser } = user.attrs;
        return safeUser;
      };

      this.passthrough('http://localhost:4000/*');

      this.get('/users/auth-user', () => {
        if (loggedInUser) {
          return new Response(
            200,
            {},
            {
              errors: [],
              data: {
                isAuthenticated: true,
                userDetails: {
                  id: loggedInUser.id,
                  firstName: loggedInUser.firstName,
                  lastName: loggedInUser.lastName,
                  fullName: loggedInUser.fullName,
                  email: loggedInUser.email,
                  phone: loggedInUser.phone,
                  country: loggedInUser.country,
                  role: loggedInUser.role,
                  isActive: loggedInUser.isActive,
                  isPhoneVerified: loggedInUser.isPhoneVerified,
                  isEmailVerified: loggedInUser.isEmailVerified,
                },
              },
            }
          );
        } else {
          return new Response(
            200,
            {},
            {
              errors: [],
              data: {
                isAuthenticated: false,
                userDetails: {},
              },
            }
          );
        }
      });

      this.post('/users/login', (schema, request) => {
        const attrs = JSON.parse(request.requestBody);
        const user = schema.users.findBy({ email: attrs.email });

        if (
          user &&
          user.password === attrs.password &&
          user.isActive !== false
        ) {
          loggedInUser = user;
          return new Response(
            200,
            {},
            {
              data: {
                token:
                  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJuYW1lIjoiSm9obiBKb2huIiwiaWQiOjEsImlhdCI6MTcwNzU0NTQ5MSwiZXhwIjoxNzA3NTQ5MDkxfQ.dxweIMZGiCuiViov1EfLtu3UwanUMp7TjL85hMDW4rc',
              },
              errors: [],
            }
          );
        } else {
          return new Response(
            404,
            {},
            {
              errors: ['User not found or invalid credentials'],
              data: {},
            }
          );
        }
      });

      this.post('/users/logout', (_schema, _request) => {
        loggedInUser = null;
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              status: 'User logged out successfully',
            },
          }
        );
      });

      this.put('/users/register', (schema, request) => {
        const attrs = JSON.parse(request.requestBody);
        const existingUser = schema.users.findBy({ email: attrs.email });

        if (existingUser) {
          return new Response(
            409,
            {},
            { errors: ['User already exists with that email'] }
          );
        } else {
          // Create a new user
          const newUser = schema.users.create({
            firstName: attrs.firstName,
            lastName: attrs.lastName,
            email: attrs.email,
            phone: attrs.phone,
            password: attrs.password,
            role: 'customer',
            isActive: true,
          });
          return new Response(
            200,
            {},
            {
              errors: [],
              user: newUser.attrs,
            }
          );
        }
      });

      this.patch('/users/update-profile', (schema, request) => {
        if (!loggedInUser) {
          return new Response(401, {}, { errors: ['Login required'] });
        }

        const attrs = JSON.parse(request.requestBody);
        const user = schema.users.find(loggedInUser.id);

        if (user) {
          const updates = {};
          if (typeof attrs.firstName === 'string') {
            updates.firstName = attrs.firstName;
          }
          if (typeof attrs.lastName === 'string') {
            updates.lastName = attrs.lastName;
          }
          if (typeof attrs.phoneNumber === 'string') {
            updates.phone = attrs.phoneNumber;
          }
          if (typeof attrs.country === 'string') {
            updates.country = attrs.country;
          }
          if (Object.keys(updates).length === 0) {
            return new Response(400, {}, { errors: ['No valid profile fields'] });
          }
          user.update(updates);
          return new Response(
            200,
            {},
            {
              errors: [],
              data: {
                status: 'Profile updated successfully',
              },
            }
          );
        } else {
          return new Response(
            404,
            {},
            {
              errors: ['User not found'],
              data: {},
            }
          );
        }
      });

      this.get('/users/bookings', () => {
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: [
                {
                  bookingId: 'BKG123',
                  bookingDate: '2024-01-10',
                  hotelName: 'Hyatt Regency Seattle',
                  checkInDate: '2024-01-20',
                  checkOutDate: '2024-01-25',
                  totalFare: '$578.00',
                },
                {
                  bookingId: 'BKG124',
                  bookingDate: '2024-01-03',
                  hotelName: 'The Westin Seattle',
                  checkInDate: '2024-02-15',
                  checkOutDate: '2024-02-20',
                  totalFare: '$1,645.00',
                },
                {
                  bookingId: 'BKG125',
                  bookingDate: '2024-01-11',
                  hotelName: 'Hyatt Regency Chicago',
                  checkInDate: '2024-03-01',
                  checkOutDate: '2024-03-05',
                  totalFare: '$1,436.00',
                },
              ],
            },
          }
        );
      });

      this.get('/users/payment-methods', () => {
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: [
                {
                  id: '1',
                  cardType: 'Visa',
                  cardNumber: '**** **** **** 1234',
                  expiryDate: '08/26',
                },
                {
                  id: '2',
                  cardType: 'MasterCard',
                  cardNumber: '**** **** **** 5678',
                  expiryDate: '07/24',
                },
                {
                  id: '3',
                  cardType: 'American Express',
                  cardNumber: '**** **** **** 9012',
                  expiryDate: '05/25',
                },
              ],
            },
          }
        );
      });

      this.get('/admin/hotels', () => {
        const denied = adminOnly();
        if (denied) return denied;

        return new Response(200, {}, { errors: [], data: { elements: hotels } });
      });

      this.post('/admin/hotels', (_schema, request) => {
        const denied = adminOnly();
        if (denied) return denied;

        const attrs = JSON.parse(request.requestBody);
        const title = attrs.title?.trim();
        const subtitle = attrs.subtitle?.trim();
        const city = attrs.city?.trim().toLowerCase();
        const price = Number(attrs.price);
        const ratings = Number(attrs.ratings);
        if (
          !title ||
          !subtitle ||
          !city ||
          !Number.isFinite(price) ||
          price <= 0 ||
          !Number.isFinite(ratings) ||
          ratings < 1 ||
          ratings > 5
        ) {
          return new Response(400, {}, { errors: ['Invalid hotel details'] });
        }

        const hotel = {
          hotelCode:
            Math.max(...hotels.map((existingHotel) => existingHotel.hotelCode)) +
            1,
          images: [{ ...hotels[0].images[0], accessibleText: title }],
          title,
          subtitle,
          city,
          price: String(price),
          ratings: String(ratings),
          benefits: (attrs.benefits || [])
            .map((benefit) => benefit.trim())
            .filter(Boolean),
        };
        hotels.push(hotel);

        return new Response(201, {}, { errors: [], data: hotel });
      });

      this.patch('/admin/hotels/:hotelId', (_schema, request) => {
        const denied = adminOnly();
        if (denied) return denied;

        const hotel = hotels.find(
          (item) => item.hotelCode === Number(request.params.hotelId)
        );
        if (!hotel) {
          return new Response(404, {}, { errors: ['Hotel not found'] });
        }

        const attrs = JSON.parse(request.requestBody);
        const title = attrs.title?.trim();
        const subtitle = attrs.subtitle?.trim();
        const city = attrs.city?.trim().toLowerCase();
        const price = Number(attrs.price);
        const ratings = Number(attrs.ratings);
        if (
          !title ||
          !subtitle ||
          !city ||
          !Number.isFinite(price) ||
          price <= 0 ||
          !Number.isFinite(ratings) ||
          ratings < 1 ||
          ratings > 5
        ) {
          return new Response(400, {}, { errors: ['Invalid hotel details'] });
        }

        hotel.title = title;
        hotel.subtitle = subtitle;
        hotel.city = city;
        hotel.price = String(price);
        hotel.ratings = String(ratings);
        hotel.benefits = (attrs.benefits || [])
          .map((benefit) => benefit.trim())
          .filter(Boolean);
        hotel.images = hotel.images.map((image) => ({
          ...image,
          accessibleText: title,
        }));

        return new Response(200, {}, { errors: [], data: hotel });
      });

      this.delete('/admin/hotels/:hotelId', (_schema, request) => {
        const denied = adminOnly();
        if (denied) return denied;

        const hotelIndex = hotels.findIndex(
          (item) => item.hotelCode === Number(request.params.hotelId)
        );
        if (hotelIndex < 0) {
          return new Response(404, {}, { errors: ['Hotel not found'] });
        }
        if (hotels.length === 1) {
          return new Response(400, {}, { errors: ['At least one hotel is required'] });
        }
        hotels.splice(hotelIndex, 1);

        return new Response(200, {}, { errors: [], data: { status: 'deleted' } });
      });

      this.get('/admin/bookings', () => {
        const denied = adminOnly();
        if (denied) return denied;

        return new Response(200, {}, {
          errors: [],
          data: { elements: adminBookings },
        });
      });

      this.patch('/admin/bookings/:bookingId', (_schema, request) => {
        const denied = adminOnly();
        if (denied) return denied;

        const booking = adminBookings.find(
          (item) => item.bookingId === request.params.bookingId
        );
        const { status } = JSON.parse(request.requestBody);
        const allowedStatuses = [
          'Confirmed',
          'Checked-in',
          'Completed',
          'Cancelled',
        ];
        if (!booking) {
          return new Response(404, {}, { errors: ['Booking not found'] });
        }
        if (!allowedStatuses.includes(status)) {
          return new Response(400, {}, { errors: ['Invalid booking status'] });
        }
        booking.status = status;

        return new Response(200, {}, { errors: [], data: booking });
      });

      this.get('/admin/users', (schema) => {
        const denied = adminOnly();
        if (denied) return denied;

        return new Response(200, {}, {
          errors: [],
          data: { elements: schema.users.all().models.map(serializeUser) },
        });
      });

      this.patch('/admin/users/:userId', (schema, request) => {
        const denied = adminOnly();
        if (denied) return denied;

        const user = schema.users.find(request.params.userId);
        const { isActive } = JSON.parse(request.requestBody);
        if (!user) {
          return new Response(404, {}, { errors: ['User not found'] });
        }
        if (typeof isActive !== 'boolean') {
          return new Response(400, {}, { errors: ['Invalid user status'] });
        }
        if (user.id === loggedInUser.id && !isActive) {
          return new Response(400, {}, { errors: ['Cannot disable own account'] });
        }

        user.update({ isActive });
        return new Response(200, {}, { errors: [], data: serializeUser(user) });
      });

      this.get('/hotel/:hotelId/booking/enquiry', (_schema, request) => {
        let hotelId = request.params.hotelId;
        const result = hotels.find((hotel) => {
          return Number(hotel.hotelCode) === Number(hotelId);
        });
        if (!result) {
          return new Response(404, {}, { errors: ['Hotel not found'] });
        }
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              name: result.title,
              cancellationPolicy: 'Free cancellation 1 day prior to stay',
              checkInTime: '12:00 PM',
              checkOutTime: '10:00 AM',
              currentNightRate: result.price,
              maxGuestsAllowed: 5,
              maxRoomsAllowedPerGuest: 3,
            },
          }
        );
      });

      this.get('/popularDestinations', () => {
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: [
                {
                  code: 1211,
                  name: 'New York',
                  imageUrl: '/images/hotels/481481762/481481762.jpg',
                },
                {
                  code: 1212,
                  name: 'Las Vegas',
                  imageUrl: '/images/hotels/465660377/465660377.jpg',
                },
                {
                  code: 1213,
                  name: 'Miami Beach',
                  imageUrl: '/images/hotels/472036509/472036509.jpg',
                },
                {
                  code: 1214,
                  name: 'Chicago',
                  imageUrl: '/images/hotels/469186143/469186143.jpg',
                },
                {
                  code: 1215,
                  name: 'San Francisco',
                  imageUrl: '/images/hotels/32810889/32810889.jpg',
                },
              ],
            },
          }
        );
      });

      this.get('/nearbyHotels', () => {
        const featuredHotels = hotels.slice(0, 5);
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: featuredHotels,
            },
          }
        );
      });

      this.get('/hotel/:hotelId', (_schema, request) => {
        let hotelId = request.params.hotelId;
        const description = [
          'A serene stay awaits at our plush hotel, offering a blend of luxury and comfort with top-notch amenities.',
          'Experience the pinnacle of elegance in our beautifully designed rooms with stunning cityscape views.',
          'Indulge in gastronomic delights at our in-house restaurants, featuring local and international cuisines.',
          'Unwind in our state-of-the-art spa and wellness center, a perfect retreat for the senses.',
          'Located in the heart of the city, our hotel is the ideal base for both leisure and business travelers.',
        ];

        const result = hotels.find((hotel) => {
          return Number(hotel.hotelCode) === Number(hotelId);
        });
        if (!result) {
          return new Response(404, {}, { errors: ['Hotel not found'] });
        }

        result.description = description;

        return new Response(
          200,
          {},
          {
            errors: [],
            data: result,
          }
        );
      });

      this.get('/hotel/:hotelId/reviews', (_schema, request) => {
        // hardcoded hotelId for now so to not add mock for each hotel
        const currentPage = request.queryParams.currentPage;
        const result = hotels.find((hotel) => hotel.reviews);
        if (!result) {
          return new Response(404, {}, { errors: ['Reviews not found'] });
        }
        const totalRatings = result.reviews.data.reduce(
          (acc, review) => acc + review.rating,
          0
        );
        const initialCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        const starCounts = result.reviews.data.reduce((acc, review) => {
          const ratingKey = Math.floor(review.rating).toString();
          if (acc.hasOwnProperty(ratingKey)) {
            acc[ratingKey]++;
          }
          return acc;
        }, initialCounts);

        const metadata = {
          totalReviews: result.reviews.data.length,
          averageRating: (totalRatings / result.reviews.data.length).toFixed(1),
          starCounts,
        };

        //paging
        const pageSize = 5;
        const paging = {
          currentPage: currentPage || 1,
          totalPages:
            Math.floor((result.reviews.data.length - 1) / pageSize) + 1,
          pageSize,
        };

        // paginated data
        const data = result.reviews.data.slice(
          (paging.currentPage - 1) * pageSize,
          paging.currentPage * pageSize
        );

        return {
          errors: [],
          data: {
            elements: data,
          },
          metadata,
          paging,
        };
      });

      this.put('/hotel/add-review', (schema, request) => {
        // const attrs = JSON.parse(request.requestBody);
        // const hotelId = attrs.hotelId;
        // const review = attrs.review;
        // const rating = attrs.rating;
        // const user = schema.users.findBy({ email: attrs.email });
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              status: 'Review added successfully',
            },
          }
        );
      });

      this.get('/hotels', (_schema, request) => {
        let currentPage = request.queryParams.currentPage;
        const filters = request.queryParams.filters;
        const parsedFilters = JSON.parse(filters);
        const parsedAdvancedFilters = JSON.parse(
          request.queryParams.advancedFilters
        );
        const city = parsedFilters.city;
        const star_ratings = parsedFilters.star_ratings;
        const priceFilter = parsedFilters.priceFilter;
        const sortByFilter = parsedAdvancedFilters.find((filter) => {
          return filter.sortBy;
        });

        const filteredResults = hotels.filter((hotel) => {
          const hotelRating = parseFloat(hotel.ratings);
          const hotelPrice = parseFloat(hotel.price.replace(',', ''));
          const isCityMatch = city === '' || hotel.city === city;
          const isPriceMatch =
            !priceFilter ||
            (hotelPrice >= parseFloat(priceFilter.start) &&
              hotelPrice <= parseFloat(priceFilter.end));

          if (isCityMatch && isPriceMatch) {
            if (star_ratings && star_ratings.length > 0) {
              return star_ratings.some((selectedRating) => {
                const selected = parseFloat(selectedRating);
                const range = 0.5;
                return Math.abs(hotelRating - selected) <= range;
              });
            } else {
              // If no star ratings are provided, return all hotels for the city (or all cities if city is empty)
              return true;
            }
          }
          return false;
        });

        if (sortByFilter) {
          const sortType = sortByFilter.sortBy;
          if (sortType === 'priceLowToHigh') {
            filteredResults.sort((a, b) => {
              return a.price - b.price;
            });
          }
          if (sortType === 'priceHighToLow') {
            filteredResults.sort((a, b) => {
              return b.price - a.price;
            });
          }
        }

        // pagination config
        const pageSize = 6;
        const totalPages =
          Math.floor((filteredResults.length - 1) / pageSize) + 1;
        currentPage = currentPage > totalPages ? totalPages : currentPage;
        const paging = {
          currentPage: currentPage || 1,
          totalPages: Math.floor((filteredResults.length - 1) / pageSize) + 1,
          pageSize,
        };

        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: filteredResults.slice(
                (paging.currentPage - 1) * pageSize,
                paging.currentPage * pageSize
              ),
            },
            metadata: {
              totalResults: filteredResults.length,
            },
            paging,
          }
        );
      });

      this.get('/availableCities', () => {
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: [
                ...new Set(hotels.map((hotel) => hotel.city)),
              ],
            },
          }
        );
      });

      this.get('/hotels/verticalFilters', () => {
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: [
                {
                  filterId: 'star_ratings',
                  title: 'Star ratings',
                  filters: [
                    {
                      id: '5_star_rating',
                      title: '5 Star',
                      value: '5',
                    },
                    {
                      id: '4_star_rating',
                      title: '4 Star',
                      value: '4',
                    },
                    {
                      id: '3_star_rating',
                      title: '3 Star',
                      value: '3',
                    },
                  ],
                },
                {
                  filterId: 'propety_type',
                  title: 'Property type',
                  filters: [
                    {
                      id: 'prop_type_hotel',
                      title: 'Hotel',
                    },
                    {
                      id: 'prop_type_apartment',
                      title: 'Apartment',
                    },
                    {
                      id: 'prop_type_villa',
                      title: 'Villa',
                    },
                  ],
                },
              ],
            },
          }
        );
      });

      this.post('/payments/confirmation', () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(
              new Response(
                200,
                {},
                {
                  errors: [],
                  data: {
                    status: 'Payment successful',
                    bookingDetails: [
                      {
                        label: 'Booking ID',
                        value: 'BKG123',
                      },
                      {
                        label: 'Booking Date',
                        value: '2024-01-10',
                      },
                      {
                        label: 'Hotel Name',
                        value: 'Hyatt Regency Seattle',
                      },
                      {
                        label: 'Check-in Date',
                        value: '2024-01-20',
                      },
                      {
                        label: 'Check-out Date',
                        value: '2024-01-25',
                      },
                      {
                        label: 'Total Fare',
                        value: '$578.00',
                      },
                    ],
                  },
                }
              )
            );
          }, 6000); // 2000 milliseconds = 2 seconds
        });
      });

      this.get('/misc/countries', () => {
        return new Response(
          200,
          {},
          {
            errors: [],
            data: {
              elements: countriesData,
            },
          }
        );
      });
    },
  });

  return server;
}
